import { AppUserService } from '@ceres/shared/services';
import { MessageService } from '@ceres/shared/services';
import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { News } from '@ceres/domain';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { rolePermissions as Permissions } from '@ceres/domain';
import { FeatureOf, FEATURES } from '@ceres/feature-toggles';
import { CeresFeaturesConfig } from '@ceres/shared/features';
import { NewsService } from '../shared/services/news.service';
import { Card } from '../shared/models';
import { HomeGuestUserService } from '../shared/services/home-guest-user.service';

@Component({
    selector: 'ceres-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
    readonly features = inject<FeatureOf<CeresFeaturesConfig>>(FEATURES);
    private messageService = inject(MessageService);
    appUserService = inject(AppUserService);
    private newsService = inject(NewsService);
    private homeGuestUserService = inject(HomeGuestUserService);

    public userName: string;
    public dialogShowing = false;

    public maxNews = 4;
    public cards: Card[] = [];
    public news: News[];
    public permissions: Set<string>;

    public destroy$: Subject<boolean> = new Subject();

    ngOnInit() {
        this.appUserService.loggedInUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
            if (user) {
                const nameParts = user.name.split(',');
                this.userName = `${nameParts[1]} ${nameParts[0]}`;
                if (user.id) {
                    void this.newsService.getByEmployee(user.details).then((news) => {
                        if (news) {
                            this.news = news;
                            news.forEach((e) => (e.collapsed = e.priority === 0));
                        }
                    });
                    this.messageGuestUser(user);
                    this.initDashboardCards();
                }
            }
        });

        this.subscribeToPermissionChanges();
    }

    private messageGuestUser(user: any) {
        if (user.role.name === 'Guest' && this.homeGuestUserService.guestSession === false) {
            this.messageService.pushMessage({
                message: 'control-center.businesspartner.auto-generated',
                title: 'Guest',
                type: 1
            });
            this.homeGuestUserService.guestSession = true;
        }
    }

    private initDashboardCards() {
        this.cards = [
            {
                title: 'my-ceres.general.tasks-title',
                icon: 'edit',
                routerLink: '/tasks',
                role: 0,
                imageUrl: '../assets/images/01_mytaks_tile.jpg',
                colorOverlay: 'orange',
                content: 'landingpage.tasks',
                permission: Permissions.task,
                features: []
            },
            {
                title: 'time-management.general.time-sheets-title',
                icon: 'clock',
                routerLink: '/timesheet',
                role: 7,
                imageUrl: '../assets/images/02_time_tile.jpg',
                colorOverlay: 'green',
                content: 'landingpage.timesheets',
                permission: Permissions.timesheet,
                features: [this.features.timeManagement.timeSheets]
            },
            {
                title: 'reporting.general.data-quality-title',
                icon: 'control',
                routerLink: '/controlling/quality-projects',
                role: 0,
                imageUrl: '../assets/images/03_qualitydashboard_tile.jpg',
                colorOverlay: 'accent-red-dark',
                content: 'landingpage.qualitydashboards',
                permission: Permissions.controlling.quality,
                features: [this.features.reporting.enabled, this.features.reporting.dataQuality]
            },
            {
                title: 'projects.general.title-accounting',
                icon: 'shopping-cart',
                routerLink: this.appUserService.isMerchantAccountingView()
                    ? '/accounting/merchant'
                    : '/accounting/overview',
                role: 0,
                imageUrl: '../assets/images/04_charging_tile.jpg',
                colorOverlay: 'accent-red-dark',
                content: 'landingpage.charging',
                permission: Permissions.project.accounting,
                features: [this.features.projects.charging]
            },
            {
                title: 'businesspartner.general.title',
                icon: 'forum',
                routerLink: '/businesspartner',
                role: 13,
                imageUrl: '../assets/images/05_business_partner_tile.jpg',
                colorOverlay: 'blue',
                content: 'landingpage.businesspartner',
                permission: Permissions.businesspartner.readAll,
                features: [this.features.businessPartner.enabled]
            },
            {
                title: 'bop.general.bop-title',
                icon: 'investor-relations',
                routerLink: '/bop',
                role: 0,
                imageUrl: '../assets/images/06_business_oppertunities_tile.jpg',
                colorOverlay: 'blue',
                content: 'landingpage.bops',
                permission: Permissions.bop.readAll,
                features: [this.features.sales.businessOpportunities, this.features.sales.enabled]
            },
            {
                title: 'offers.general.title',
                icon: 'information-package',
                routerLink: '/contract',
                role: 8,
                imageUrl: '../assets/images/07_offers_tile.jpg',
                hoverImageUrl: '../assets/images/07_offers_tile.jpg',
                colorOverlay: 'blue',
                content: 'landingpage.offers',
                permission: Permissions.contract.readAll,
                features: [this.features.sales.offers, this.features.sales.enabled]
            },
            {
                title: 'projects.general.title-projects',
                icon: 'feature',
                routerLink: '/projects',
                role: 1,
                imageUrl: '../assets/images/08_projects_tile.jpg',
                colorOverlay: 'blue',
                content: 'landingpage.projects',
                permission: [Permissions.project.readAll, Permissions.project.readOwn],
                features: [this.features.projects.enabled]
            },
            {
                title: 'projects.general.new-project',
                icon: 'information-package',
                routerLink: '/projects/new',
                role: 1,
                imageUrl: '../assets/images/08_projects_tile.jpg',
                colorOverlay: 'orange',
                content: 'landingpage.new-project',
                permission: [Permissions.guest],
                features: [this.features.projects.enabled]
            },
            {
                title: 'services.general.title',
                icon: 'shopping-cart',
                routerLink: '/services',
                role: 1,
                imageUrl: '../assets/images/08_projects_tile.jpg',
                colorOverlay: 'blue',
                content: 'landingpage.services',
                permission: Permissions.services.readAll,
                features: [this.features.services.enabled]
            },
            {
                title: 'admin.general.admin-title',
                icon: 'settings',
                routerLink: '/admin',
                role: 12,
                imageUrl: '../assets/images/08_admin_tile.jpg',
                colorOverlay: 'blue',
                content: 'landingpage.admin',
                permission: Permissions.admin.access,
                features: [this.features.administration.enabled]
            }
        ];
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    private subscribeToPermissionChanges() {
        this.appUserService.permissions$
            .pipe(takeUntil(this.destroy$))
            .subscribe((permissions: any) => (this.permissions = permissions));
    }
}
